import { useEffect, useMemo, useState } from 'react';
import apiService from '../../services/api';
import { computeContractSignatoryGateMissing } from '../../utils/nr2jConstants';
import { contractSendGateLabel } from '../../utils/nr2jContractGateUi';

/**
 * Complétion inline d’un Nr2jContact (signataire contrat) sans quitter l’éditeur.
 */
export default function Nr2jCompleteContactModal({
  open,
  contact,
  onClose,
  onSaved,
}) {
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [fonction, setFonction] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !contact) return;
    setPrenom(contact.prenom || '');
    setNom(contact.nom || '');
    setEmail(contact.email || '');
    setFonction(contact.fonction || '');
    setError('');
  }, [open, contact]);

  const missingKeys = useMemo(
    () => computeContractSignatoryGateMissing(contact),
    [contact],
  );

  const showNameFields = true;
  const showEmail = true;
  const showFonction = true;

  if (!open || !contact) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const payload = {};
      if (showNameFields) {
        payload.prenom = prenom.trim();
        payload.nom = nom.trim();
      }
      if (showEmail) payload.email = email.trim();
      if (showFonction) payload.fonction = fonction.trim();

      const res = await apiService.updateNr2jContact(contact.id, payload);
      const updated = res?.contact || res;
      onSaved?.(updated);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="nr2j-complete-contact-modal"
    >
      <form
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
        onSubmit={handleSubmit}
      >
        <h3 className="text-lg font-semibold text-gray-900">Compléter le contact signataire</h3>
        <p className="mt-1 text-sm text-gray-600">
          Les données sont enregistrées sur la fiche contact. Le brouillon contrat reste inchangé.
        </p>
        {missingKeys.length ? (
          <ul className="mt-3 space-y-1 text-sm text-amber-900">
            {missingKeys.map((key) => (
              <li key={key} data-testid="nr2j-complete-contact-missing-item">
                ⚠ {contractSendGateLabel(key)} — à compléter avant envoi
              </li>
            ))}
          </ul>
        ) : null}
        <div className="mt-4 space-y-3">
          {showNameFields ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="text-gray-500">Prénom</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={prenom}
                  onChange={(ev) => setPrenom(ev.target.value)}
                  data-testid="nr2j-complete-contact-prenom"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-500">Nom</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={nom}
                  onChange={(ev) => setNom(ev.target.value)}
                  data-testid="nr2j-complete-contact-nom"
                />
              </label>
            </div>
          ) : null}
          {showEmail ? (
            <label className="block text-sm">
              <span className="text-gray-500">Email</span>
              <input
                type="email"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                data-testid="nr2j-complete-contact-email"
              />
            </label>
          ) : null}
          {showFonction ? (
            <label className="block text-sm">
              <span className="text-gray-500">Fonction</span>
              <input
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                value={fonction}
                onChange={(ev) => setFonction(ev.target.value)}
                placeholder="Ex. Gérant"
                data-testid="nr2j-complete-contact-fonction"
              />
            </label>
          ) : null}
        </div>
        {error ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            data-testid="nr2j-complete-contact-save"
          >
            Enregistrer le contact
          </button>
        </div>
      </form>
    </div>
  );
}
